import { std } from '@funkey/shared/utils';
import { isNearPointActive } from './nearPoint';

/** Every `FilterLayout` id used in `ActivityFiltersContent`. */
export const ACTIVITY_FILTER_SECTION_IDS = [
    'sort',
    'language',
    'budget',
    'participants',
    'categories',
    'duration',
    'location',
    'locationType',
    'traits',
    'season',
    // 'accessibilityAndAudience',  // commented out (re-enable after merge)
] as const;

/** Count the number of active filter values, excluding `sort` and `countryCode`. */
export const getActiveFilterCount = (
    value: Record<string, unknown> & {
        nearCleared?: boolean | null;
        nearLat?: number | null;
        nearLng?: number | null;
    },
) => {
    let count = isNearPointActive(value) ? 1 : 0;

    for (const filterValue of Object.values(
        std.Object.omit(value, [
            'sort',
            'countryCode',
            'nearCleared',
            'nearLat',
            'nearLng',
            'nearLabel',
            'radius',
        ]),
    )) {
        if (filterValue === null || filterValue === undefined) continue;
        if (typeof filterValue === 'string' && filterValue.length === 0) continue;

        if (Array.isArray(filterValue)) count += filterValue.length;
        else count += 1;
    }

    return count;
};

// ---------------------------------------------------------------------------
// Budget & VAT badge / clear helpers
// ---------------------------------------------------------------------------

type BudgetVATContext = {
    isBizz: boolean;
    bizzVATInclusive: boolean;
    bizzVATRate: number | null;
    minBudget: number | null;
    maxBudget: number | null;
    vatPrefEnabled: boolean;
    vatPrefRate: number;
};

/** Derive the badge string for the Budget & VAT filter section. */
export function getBudgetVATBadge(ctx: BudgetVATContext): string | undefined {
    const hasBudget = !!(ctx.minBudget || ctx.maxBudget);
    const hasVAT = ctx.isBizz ? ctx.bizzVATInclusive : ctx.vatPrefEnabled;
    const vatRate = ctx.isBizz ? (ctx.bizzVATInclusive ? ctx.bizzVATRate : null) : ctx.vatPrefRate;

    const parts = [
        ...(hasBudget ? [`€${ctx.minBudget ?? 0} – €${ctx.maxBudget ?? '∞'}`] : []),
        ...(hasVAT && vatRate ? [`${vatRate}%`] : []),
    ];
    const badge = parts.join(', ');
    return badge || undefined;
}

/** Whether the clear/reset button should be shown for Budget & VAT. */
export function isBudgetVATClearable(ctx: BudgetVATContext): boolean {
    const hasBudget = !!(ctx.minBudget || ctx.maxBudget);
    const hasVAT = ctx.isBizz ? ctx.bizzVATInclusive : ctx.vatPrefEnabled;
    return hasBudget || (!ctx.isBizz && hasVAT);
}
